import CONFIG from 'Config/settings';
import { push } from 'connected-react-router';
import JwtDecode from 'jwt-decode';
import { ReduxStore } from 'Redux';
import {
  setLoginSuccessAction,
  setPartyUidAction
} from 'Redux/auth/authActions';
import { clearFormAction, setFormErrorsAction } from 'Redux/forms/formsActions';
import {
  setCoworkerLinkingStatusAction,
  setUserIdAction
} from 'Redux/profile/profileActions';
import { loadingAction } from 'Redux/userInterface/userInterfaceActions';
import { LOADING } from 'Redux/userInterface/userInterfaceConstants';
import {
  setEmailVerifiedAction,
  setLoginIdentifierAction
} from 'Redux/verification/verificationActions';
import Routes from 'Routes/profileRoutes';
import { Auth0Service, AuthService } from 'Services/ServiceFactory';
import analytics from 'Utils/analytics/analyticsWrapper';
import {
  appendAuthTokenToUrl,
  getCoworkerLinkingStatusFromQueryStrings,
  getRedirectUrlFromQueryStrings,
  isPopup,
  readAuthTokenCookie,
  readWindowAuthTokens,
  writeSessionCookie
} from 'Utils/auth/authUtils';
import { isUTCDateInPast } from 'Utils/date/dateUtils';
import { mapBackendException } from 'Utils/errors/errorUtils';
import { events, publishEvent } from 'Utils/pubsub';
import { captureSentry } from 'Utils/sentry';
import { isValidIKEAUrl } from 'Utils/url/urlUtils';

const redirectToUrl = async url => {
  if (isPopup()) {
    await analytics.ciePagePopup('login_form', 'login_page_popup_success');
    const currentWindow = window.self;
    currentWindow.opener.location.assign(url);
    currentWindow.opener = window.self;
    currentWindow.close();
  } else {
    window.location = url;
  }
};

export const getSession = async () => {
  const authResp = await AuthService.isAuthenticated();
  if (!(authResp && authResp.authenticated)) {
    throw new Error();
  }
  return authResp;
};

export const login = (
  loginQueryStrings,
  _bvEventId,
  internalRedirect,
  externalRedirect,
  from = window.location.href.includes(CONFIG.APP.BASE_NAME)
    ? window.location.href
    : '',
  _formId,
  icw
) => {
  return async dispatch => {
    try {
      let sessionResp;

      // Case 1:
      // User is redirected back from CIAM hosted login page with access
      // token and id token after user has provided valid credentials.
      if (loginQueryStrings) {
        const authResp = await Auth0Service.handleAuthentication(
          loginQueryStrings
        );

        if (!authResp) {
          window.location = Routes.LOGIN;
          return;
        }

        publishEvent(events.LOGIN);

        sessionResp = await getSession();

        if (window.ikea.cookieConsent?.hasConsent(3)) {
          const firstNameInitial = sessionResp.user.firstName
            ? sessionResp.user.firstName.charAt(0).toUpperCase()
            : undefined;
          const lastNameInitial = sessionResp.user.lastName
            ? sessionResp.user.lastName.charAt(0).toUpperCase()
            : undefined;
          const nameInitials =
            firstNameInitial && lastNameInitial
              ? `${firstNameInitial}${lastNameInitial}`
              : '';
          await writeSessionCookie(
            JSON.stringify({
              firstName: sessionResp.user.firstName,
              nameInitials,
              preferredStore: sessionResp.user.preferredStore,
              zipCode: sessionResp.user.zipCode
            })
          );

          await publishEvent(events.USER_INFO_AVAILABLE);
        }

        const redirectUrlFromQueryStrings = getRedirectUrlFromQueryStrings(
          loginQueryStrings
        );
        if (redirectUrlFromQueryStrings) {
          redirectToUrl(appendAuthTokenToUrl(redirectUrlFromQueryStrings));
          return;
        }
        // Don't redirect to loyalty-hub in case of coworker link
        if (!getCoworkerLinkingStatusFromQueryStrings(loginQueryStrings)) {
          redirectToUrl(CONFIG.URL.IKEA_FAMILY_HUB);
          return;
        }
      }
      const authToken =
        (await readWindowAuthTokens()) || (await readAuthTokenCookie());
      const authTokenExist =
        authToken && !isUTCDateInPast(JwtDecode(authToken).exp);
      const validSession = authTokenExist;

      // Case 2:
      // User does not have a valid session (token) and will be redirected
      // to CIAM hosted login page.
      if (!validSession) {
        const redirectTo = isValidIKEAUrl(from) ? from : undefined;
        Auth0Service.login(redirectTo, undefined, icw);
        return;
      }

      // Case 3:
      // User has a valid session (token).
      sessionResp = sessionResp || (await getSession());

      if (!sessionResp.authenticated) {
        redirectToUrl(Routes.LOGIN);
        return;
      }
      // User is logged in and authenticated.
      await dispatch(setLoginSuccessAction(sessionResp.user.userLogin));
      await dispatch(setUserIdAction(sessionResp.user.userId));
      const coworkerLinkingStatus = sessionResp.user.employeeId
        ? icw || getCoworkerLinkingStatusFromQueryStrings(loginQueryStrings)
        : undefined;
      await dispatch(setCoworkerLinkingStatusAction(coworkerLinkingStatus));
      await dispatch(setPartyUidAction(sessionResp.user.partyUid));
      await dispatch(setEmailVerifiedAction(sessionResp.user.emailVerified));
      await dispatch(setLoginIdentifierAction(sessionResp.user.identifier));

      const feature =
        CONFIG.FEATURE[
          `${sessionResp.user.profileType.toUpperCase()}_DASHBOARD`
        ];

      if (!feature.USE_APPLICATION && feature.USE_EXTERNAL) {
        redirectToUrl(feature.EXTERNAL_URL);
        return;
      }

      if (internalRedirect) {
        if (isValidIKEAUrl(from)) {
          const redirectUrl = appendAuthTokenToUrl(from);
          redirectToUrl(redirectUrl);
          return;
        }
        // Don't redirect to loyalty-hub in case of coworker link
        if (!coworkerLinkingStatus) {
          redirectToUrl(CONFIG.URL.IKEA_FAMILY_HUB);
          return;
        }
        // eslint-disable-next-line no-unused-expressions
        isPopup()
          ? redirectToUrl(
              `${window.location.origin}/${CONFIG.COUNTRY}/${CONFIG.LANGUAGE}/profile${internalRedirect}`
            )
          : dispatch(push(internalRedirect));
      }
      if (externalRedirect) {
        redirectToUrl(externalRedirect);
        return;
      }
    } catch (e) {
      captureSentry(e, { extra: { from, internalRedirect, externalRedirect } });
      window.location = Routes.ERROR;
    }
  };
};

export const hasValidSession = () => {
  return async () => {
    const authResp = await AuthService.isAuthenticated();
    return authResp.authenticated;
  };
};

export const setNewPassword = (
  { currentPassword, newPassword, newPasswordRetype },
  formId,
  from
) => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, true));
      const { userId } = ReduxStore.getState().profile;
      const { userLogin } = ReduxStore.getState().auth;
      const data = {
        userId,
        currentPassword,
        newPassword,
        newPasswordRetype,
        ...(formId === 'reset-password-form' && { userLogin })
      };

      await AuthService.setNewPassword(data);

      if (formId === 'reset-password-form') {
        await dispatch(
          login(
            { username: userLogin, password: newPassword },
            null,
            Routes.ROOT,
            null,
            from,
            formId
          )
        );
      }
      dispatch(clearFormAction(formId));
      return true;
    } catch (e) {
      dispatch(setFormErrorsAction(formId, mapBackendException(e)));
      return false;
    } finally {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, false));
    }
  };
};
